import { collection, query, where, getDocs, doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Convert provisional licenses to full allocated licenses when seats become available.
 * Called client-side when the organization subscription page loads or after a seat upgrade.
 * 
 * @param {string} orgId - The organization ID
 * @returns {Promise<number>} Number of provisional licenses converted
 */
export const convertProvisionalLicenses = async (orgId) => {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    const allocationsRef = collection(db, 'organizations', orgId, 'licenseAllocations');
    const allocationHistoryRef = collection(db, 'organizations', orgId, 'licenseAllocationHistory');

    // Find all provisional licenses that haven't expired
    const provisionalQuery = query(
      allocationsRef,
      where('status', '==', 'provisional')
    );
    const provisionalSnap = await getDocs(provisionalQuery);

    if (provisionalSnap.empty) return 0;

    // Sort by allocatedAt (oldest first) to be fair
    const provisionalDocs = provisionalSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(d => {
        // Only convert those that haven't expired
        if (!d.expiresAt) return true;
        const expiry = d.expiresAt.toDate ? d.expiresAt.toDate() : new Date(d.expiresAt);
        return expiry > new Date();
      })
      .sort((a, b) => {
        const aTime = a.allocatedAt?.toDate ? a.allocatedAt.toDate().getTime() : 0;
        const bTime = b.allocatedAt?.toDate ? b.allocatedAt.toDate().getTime() : 0;
        return aTime - bTime;
      });

    if (provisionalDocs.length === 0) return 0;

    let converted = 0;

    // Convert one at a time within transactions
    for (const provisionalAlloc of provisionalDocs) {
      try {
        const success = await runTransaction(db, async (transaction) => {
          const orgSnap = await transaction.get(orgRef);
          if (!orgSnap.exists()) return false;

          const orgData = orgSnap.data();
          const license = orgData?.license || {};
          const totalSeats = license.disciplemakers ?? license.seats ?? 0;
          const usedSeats = license.disciplemakersUsed ?? license.seatsUsed ?? 0;
          const available = totalSeats - usedSeats;

          if (available <= 0) return false;

          const allocationRef = doc(allocationsRef, provisionalAlloc.id);
          const memberRef = doc(db, 'organizations', orgId, 'members', provisionalAlloc.userId || provisionalAlloc.id);

          // Convert provisional to allocated
          transaction.update(allocationRef, {
            status: 'allocated',
            convertedAt: serverTimestamp(),
            convertedFrom: 'provisional',
            expiresAt: null,
          });

          // Update member doc
          transaction.update(memberRef, {
            licenseStatus: 'allocated',
            licenseExpiresAt: null,
          });

          // Increment seat usage
          const newUsed = usedSeats + 1;
          const newAvailable = totalSeats - newUsed;
          transaction.update(orgRef, {
            'license.disciplemakersUsed': newUsed,
            'license.disciplemakersAvailable': newAvailable,
            'license.seatsUsed': newUsed,
            'license.seatsAvailable': newAvailable,
            'license.updatedAt': serverTimestamp(),
          });

          // Log history
          const historyDocRef = doc(allocationHistoryRef);
          transaction.set(historyDocRef, {
            userId: provisionalAlloc.userId || provisionalAlloc.id,
            action: 'converted',
            fromStatus: 'provisional',
            toStatus: 'allocated',
            reason: 'seats_available',
            organizationId: orgId,
            timestamp: serverTimestamp(),
          });

          return true;
        });

        if (success) {
          converted++;
        } else {
          break; // No more seats available
        }
      } catch (txError) {
        console.warn('Failed to convert provisional license:', txError);
        break;
      }
    }

    return converted;
  } catch (error) {
    console.error('Error converting provisional licenses:', error);
    return 0;
  }
};
